import { BookingLocationType } from '@navio/nsdk';
import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import { CHANNEL_ICON, CHANNEL_LABEL } from './ChannelBadge';

/**
 * One channel as a toggle, for surfaces where a record can be in both lanes at once and the
 * segmented `ChannelSwitcher` (single-select) does not fit. Neutral at rest like every other
 * chip; on, it takes that channel's own tint — the same pair the badge and switcher use, so a
 * channel keeps one color across the app.
 */
const channelChipVariants = cva(
  'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-hidden',
  {
    variants: {
      channel: {
        [BookingLocationType.STATIONARY]: '',
        [BookingLocationType.MOBILE]: ''
      },
      selected: {
        true: 'font-medium',
        false: 'border-border bg-card text-grey md:hover:bg-muted'
      }
    },
    compoundVariants: [
      {
        channel: BookingLocationType.STATIONARY,
        selected: true,
        className: 'border-channel-stationary-700 bg-channel-stationary-100 text-channel-stationary-800'
      },
      {
        channel: BookingLocationType.MOBILE,
        selected: true,
        className: 'border-channel-mobile-700 bg-channel-mobile-100 text-channel-mobile-800'
      }
    ],
    defaultVariants: { selected: false }
  }
);

type Props = {
  channel: BookingLocationType;
  selected: boolean;
  onClick: () => void;
  /** Overrides the default channel name — use only where the surface already implies "channel". */
  label?: string;
  title?: string;
  className?: string;
};

export const ChannelChip = ({ channel, selected, onClick, label, title, className }: Props) => (
  <button
    type='button'
    aria-pressed={selected}
    onClick={onClick}
    title={title}
    className={cn(channelChipVariants({ channel, selected }), className)}
  >
    <NoddiIcon name={CHANNEL_ICON[channel]} size='small' />
    {label ?? CHANNEL_LABEL[channel]}
  </button>
);
